import { ReactNode, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { LineItemPresenter, OutletPresenter } from 'shared/presenters'
import LineItemBooking from './LineItemBooking'
import LineItemClassPass from './LineItemClassPass'
import LineItemCreditVoucher from './LineItemCreditVoucher'
import LineItemMembership from './LineItemMembership'
import LineItemActivityVoucher from './LineItemActivityVoucher'
import LineItemArbitraryTransaction from './LineItemArbitraryTransaction'
import LineItemPurchasedStoreItem from './LineItemPurchasedStoreItem'
import { MEMBERSHIPS_CHARGEABLE_TYPE } from 'shared/enums/memberships'
import styled from '@emotion/styled'
import Flex from 'shared/library/atoms/Flex'

interface LineItemListProps {
  /**
   * There are two variants for LineItem, the default value has more space between each item and
   * the style stands out more e.g. Basket page, the contained version is simpler / cleaner and is meant for being
   * displayed inside a container e.g. Checkout page.
   * */
  variant?: 'default' | 'contained'
  children: ReactNode
}

const lineItemComponentMap = {
  BOOKING: LineItemBooking,
  PURCHASEDCLASSPASS: LineItemClassPass,
  CREDITVOUCHER: LineItemCreditVoucher,
  [MEMBERSHIPS_CHARGEABLE_TYPE.toUpperCase()]: LineItemMembership,
  ACTIVITYVOUCHER: LineItemActivityVoucher,
  ARBITRARYTRANSACTION: LineItemArbitraryTransaction,
  PURCHASEDSTOREITEM: LineItemPurchasedStoreItem
}

export interface LineItemInterface {
  outlet: OutletPresenter
  item: LineItemPresenter
  removeItemOnClick?: (id: string) => void
  removeItemLoading?: boolean
  completeActivityVoucherBooking?: (params: {
    widgetSlug?: string
    bookingId?: string
    schedulableSlug?: string
  }) => void
  setExpanded?: (expanded: boolean) => void
  expanded?: boolean
  expand?: boolean
}

/**
 * LineItem index to be used as a means of rendering lineItems of various types.
 * Used as a means of reducing logic at a page level i.e. basket / checkout pages in .map cycles where
 * we would otherwise have to decide on what individual line item component to render / take the approach
 * of making a single line item component to deal with everything - vouchers, one off purchases, bookings
 * etc.
 *
 * This way we will map and render the lineItem component which will then render the specific component
 * needed. See basket page for it in use.
 */
const LineItem = ({
  item,
  removeItemOnClick: removeBasketItemOnClick,
  removeItemLoading: removeBasketItemLoading,
  expand = true,
  ...props
}: LineItemInterface) => {
  const [removeInProgress, setRemoveInProgress] = useState(
    removeBasketItemLoading
  )
  const [expanded, setExpanded] = useState(expand)

  useEffect(() => {
    if (!removeBasketItemLoading) {
      setRemoveInProgress(false)
    }
  }, [removeBasketItemLoading])

  if (
    !item?.chargeable_type ||
    !Object.keys(lineItemComponentMap).includes(
      item.chargeable_type.toUpperCase()
    )
  ) {
    Sentry.captureException(
      `LineItemIndex - cannot find component to render for ${item.chargeable_type}`
    )

    return null
  }

  const removeItem = removeBasketItemOnClick
    ? (id: string) => {
        if (!removeBasketItemOnClick || removeInProgress) {
          return
        }
        setRemoveInProgress(true)
        removeBasketItemOnClick(id)
      }
    : undefined

  const ComponentToRender =
    lineItemComponentMap[
      item.chargeable_type.toUpperCase() as keyof typeof lineItemComponentMap
    ]

  return (
    <ComponentToRender
      item={item}
      removeItemOnClick={removeItem}
      removeItemLoading={removeInProgress}
      {...props}
      setExpanded={setExpanded}
      expanded={expanded}
    />
  )
}

export default LineItem

export const StyledLineItem = styled.li(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  background: theme.colors.white,
  transition: `all ${theme.transitionTime[1]}s ${theme.transitionTimingFunction.smooth}`
}))

export const StyledFlex = styled(Flex)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  marginLeft: theme.space[1],
  justifyContent: 'space-between',
  alignItems: 'flex-end'
}))

export const StyledDetailsFlex = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  width: '100%',
  marginLeft: theme.space[1],
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  borderRadius: theme.radii[2],
  padding: theme.mixins.pxSpread([theme.space[1], theme.space[2]])
}))

export const StyledInfoFlex = styled(Flex)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
  marginBottom: theme.space[1]
}))

/**
 * There are two variants for LineItem, the default value has more space between each item and
 * the style stands out more e.g. Basket page, the contained version is simpler / cleaner and is meant for being
 * displayed inside a container e.g. Checkout page.
 * */
export const LineItemList = styled.ul<LineItemListProps>(
  () => ({
    display: 'flex',
    flexWrap: 'wrap' as const,
    listStyleType: 'none',
    margin: 0
  }),
  ({ theme, variant }) =>
    variant === 'contained'
      ? {
          padding: theme.space[4],
          border: theme.border,
          borderRadius: theme.radii[3],
          boxShadow: theme.shadows.primary[0],
          background: theme.colors.white,
          [`${StyledLineItem}`]: {
            padding: theme.mixins.pxSpread([theme.space[4], 0]),
            '& + li': {
              borderTop: theme.border
            }
          }
        }
      : {
          padding: 0,
          [`${StyledLineItem}`]: {
            padding: theme.space[4],
            border: theme.border,
            borderRadius: theme.radii[3],
            boxShadow: theme.shadows.primary[0],
            '&:hover': {
              transform: `translateY(-${theme.space[1]}px)`,
              boxShadow: theme.shadows.primary[1]
            },
            '& + li': {
              marginTop: theme.space[4]
            }
          }
        }
)
